import React, { useState } from 'react';
import { useOverviewContext } from '@/context/OverviewContext';
import { useLinksContext } from '@/context/LinksContext';
import { useToolSettings } from '@/context/ToolSettingsContext';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { getContentByGrade, isMiddleOrHighSchool } from '@/utils/getContentByGradeUtils';
import { getActiveToolIDs, shouldShowK12Zone } from '@/utils/toolSettingsUtils';
import { cleanUrl, renderConditionalLink  } from '@/utils/linksUtils';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import SubPageHero from '@/components/SubPageHero/SubPageHero';
import { RedirectIcon } from '@/components/MenusIcons/MenusIcons';
import Card from '@/components/Card/Card';
import StackedContent from '@/components/StackedContent/StackedContent';
import FAQSection from '@/components/FAQSection/FAQSection';
import Button from '@/components/Button/Button';
import { Image } from 'react-bootstrap';
import config from '@/config';
import heroUnion from '@/assets/images/computer_school_item.svg';

// Section-stacked-1
import lcImage from '@/assets/images/k12-school-student-setupguide.svg';
import stImage from '@/assets/images/st_k12_getting_started_checklist.png';
import pdfStChecklist from '@/assets/pdfs/ST-K12-School-Student-Getting-Started-Guide.pdf';
import pdfLcStGuide from '@/assets/pdfs/K12-School-Student-Getting-SetupGuide.pdf';

/// VerticalCards
import K5NavigateImg from '@/assets/images/get_comfortable_card_img.svg';
import sampleLessonImg from '@/assets/images/sample_lesson_card_img.svg';
import redirectLinkIcon from '@/assets/icons/canvas-redirect-link.svg';

// Section-2 stacked
import lcSupportImage from '@/assets/images/lc-support-student.svg';
import pdfLcSupport from '@/assets/pdfs/LC-SupportStudent-K12.pdf';
// FAQ
import pdfSchoolFAQ from '@/assets/pdfs/School-Learning-Coach-FAQs.pdf';
import OlsVideoSection from '@/components/OlsVideoSection/OlsVideoSection';
import GettingStartedSection from '@/components/GettingStartedSection/GettingStartedSection';
import ButtonSet from '@/components/ButtonSet/ButtonSet';
import VidyardPlayer from '@/components/VidyardPlayer/VidyardPlayer';
import { useTasklistDisplayRuntime } from '@/hooks/useTasklistDisplayRuntime/useTasklistDisplayRuntime';

const NavigatingK12School: React.FC = () => {
    const breadcrumbItems = [
        { title: "Home", path: "/" },
        { title: "School Information", path: "/schoolinfo" },
        { title: "Navigating K12 School (OLS)" }
    ];
    const { SCHOOL_START_FAQ } = config.app.routes;
    const { userRoleVal, formattedStudentNames = "" } = useOverviewContext();
    const { selectedStudent } = useSelectedStudent();
    const studentGrade = selectedStudent?.gradeId;
    const survivalProps = studentGrade ? getContentByGrade(studentGrade, 'survival') : null;
    const lessonProps = studentGrade ? getContentByGrade(studentGrade, 'verticalCardsLesson') : null;
    const { k12ZoneLink, schoolLink } = useLinksContext();
    const {
      k12SchoolHref,
      showK12SchoolCta,
      showCanvasInlineLinks,
      isReady: k12PortalReady,
    } = useTasklistDisplayRuntime();
    const { toolSettings } = useToolSettings();
    const activeToolIDs = getActiveToolIDs(toolSettings);
    const showK12Zone = shouldShowK12Zone(userRoleVal, activeToolIDs);
    const cleank12ZoneURL = k12ZoneLink?.trim().replace(/^"|"$/g, "") || undefined;
    const isStudent = userRoleVal === 'STUDENT';
    const k12ButtonText = isStudent ? 'Enter the K12 Zone' : 'Enter the Visitor Zone';
    const displayK12ZoneLink = isStudent ? cleank12ZoneURL : config.LINKS.K12Zone_URL;
    const showCalendarAsLink = k12PortalReady && showCanvasInlineLinks;
    const calendarLink =
      showCalendarAsLink && schoolLink.schoolLinkUrl
        ? `${cleanUrl(schoolLink.schoolLinkUrl)}calendar`
        : undefined;

    const olsButtonLabel = k12SchoolHref ? "Go to K12 School (OLS)" : "K12 School (OLS) Available soon";
    const olsCardLabel = k12SchoolHref ? "Explore on Your Own" : "K12 School (OLS) Available soon";
    const olsCardIcon = k12SchoolHref ? <RedirectIcon /> : undefined;
    const inlineSchoolHref =
      k12PortalReady && showK12SchoolCta && k12SchoolHref ? k12SchoolHref : undefined;
    const [showVideo, setShowVideo] = useState(false);
    const videoSchoolFAQ = "gccooUPcsTy3avNpweSreg";
    const videoUrlFAQ = "https://share.vidyard.com/watch/gccooUPcsTy3avNpweSreg?";
    const handleOpenLightbox = () => {
    if (window.VidyardV4?.api) {
        const players = window.VidyardV4.api.getPlayersByUUID(videoSchoolFAQ);
        players?.[0]?.showLightbox();
    }
    };

    const onlineSchoolFaqs = [
    {
            faq_title: "My login is not working. What do I do?",
            faq_content: (
                <>
                    {isStudent
                        ? "Ask your Learning Coach for your username and password. If necessary, your Learning Coach can contact your school’s enrollment department for assistance or contact the "
                        : "Contact your school’s enrollment department for assistance or contact the "}
                    <a
                        href="https://help.k12.com"
                        style={{ color: "#000", fontWeight: 700 }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        K12 Help Desk
                    </a>
                    .
                </>
            ),
        },
        {
            faq_title: isStudent ? "How do I join a live class?" : "How does my student join a live class?",
            faq_content: (
                <>
                    When {isStudent ? "your" : "your student's"} courses are assigned, you can use the Calendar in the K12 School (OLS) to view class times and join live Class Connect sessions.
                </>
            ),
        },
        {
            faq_title: isStudent ? "How do I contact my teacher?" : "How do I message my child's teacher?",
            faq_content: (
                <>
                    {isStudent
                        ? `You can use the Inbox on the left side of the screen in the K12 School (OLS) to send a message to your teacher. Click “Compose a new message” on the top right of the screen and choose the course to find the teacher you wish to contact.`
                        : `Use the Canvas Syllabus or Course Materials Module to locate the teacher’s contact information. Send an email from your preferred email address to the teacher. You may see a notification in the Canvas Inbox tool; however, all messages and replies should be sent through the email client directly.`}
                </>
            ),
        },
        {
            faq_title: isStudent ? "Canvas is running slowly or freezing. What do I do?" : "How do I know when assignments are due?",
            faq_content: isStudent ? (
                <>Try clearing your browser’s cache and cookies, updating the browser, or switching to another browser.</>
            ) : (
                <>
                    You can find due dates for any graded items in several places in Canvas! All items with due dates will be shown on the course{" "}
                    <strong>Calendar</strong>. Check the <strong>Syllabus</strong> as well for a list of all graded items and their due dates.
                    Finally, navigate to <strong>Modules</strong>, where you can find the lessons and assignments with due dates.
                </>
            ),
        },
    ];

    // Section-1 role-specific UI Rendering
    const gettingStartedHeader = "Getting Started in the K12 School (OLS)";
    const gettingStartedDescription = (
    <>
            On the first day of school, your student will need to log into their {" "}
            
            <b>
            {renderConditionalLink(
                "K12 School (OLS)",
                inlineSchoolHref,
                "canvas-link",
                { color: "#1C1C1F" }
            )}
            </b>
            {" "} account to see their schedule, attend class, and check their email. <br /><br />
            
            Having trouble with lag time, missing information, or error messages when logging in? &nbsp;
            <b><a href="https://share.vidyard.com/watch/8X62HpNFjSMUKQF4RL87dM"
            target="_blank" rel="noopener noreferrer" style={{ color:"#1C1C1F"}} >
            Watch this video 
            </a></b>{" "} to learn how to clear your cache and cookies for better performance.
        </>
    );
    const gettingStartedRoleUI = {
    STUDENT: {
        media: stImage,
        buttonText: "Setup Checklist for Students",
        linkUrl: pdfStChecklist,
    },
    LEARNING_COACH: {
        media: lcImage,
        buttonText: "Download the Setup Guide",
        linkUrl: pdfLcStGuide,
    },
    } as const;
    const roleKey = isStudent ? "STUDENT" : "LEARNING_COACH";
    const roleUI = gettingStartedRoleUI[roleKey];

    // Section-2
    const roleOLSContent = {
    STUDENT: {
        header: "How the K12 School (OLS) Works ",
        description: (
        <>
            When you enter through the K12 School Login (OLS), everything you need for school is there.
            You'll be expected to keep up with school communication and your assigned classes and school work.
            The K12 School (OLS) allows you to:
            <ul>
            <li>Check for announcements</li>
            <li>Explore assignments</li>
            <li>Navigate courses</li>
            <li>Participate in discussions</li>
            <li>Check your grades</li>
            <li>Set up notifications</li>
            <li>Take quizzes</li>
            </ul>
            <p className="body-m">Read the full setup guide to get started.</p>
        </>
        ),
        buttonText: "Read the Full Setup Guide",
        linkUrl: pdfLcStGuide, //same setupGuide for both
        media: lcImage,  //same Image for both
    },
    LEARNING_COACH: {
        header: "Supporting Your Student as Learning Coach in the K12 School (OLS) ",
        description: (
        <>
            As <strong> {formattedStudentNames} </strong>'s Learning Coach, you'll use your K12 School (OLS) account to complete a few ongoing key actions in support of their learning, including:
            <ul style={{ marginTop: '1rem' }}>
            <li>Monitoring student progress</li>
            <li>Checking grades</li>
            <li>Reviewing teacher feedback</li>
            <li>Checking the calendar</li>
            <li>Communicating with the teacher</li>
            </ul>
            <p className="body-m"><b><a href="https://share.vidyard.com/watch/LnCSba9Qu7LhME52P85h1w"
            target="_blank" rel="noopener noreferrer" style={{ color:"#1C1C1F"}} > Watch this video </a></b>{" "} or read this guide to help you prepare for the start of school. 
            </p>
        </>
        ),
        buttonText: "Download the Support Guide",
        linkUrl: pdfLcSupport,
        media: lcSupportImage,
    },
    } as const;
    const {
    header: olsHeader,
    description: olsDescription,
    buttonText: olsButtonText,
    media: olsMedia,
    linkUrl: olsLinkUrl,
    } = isStudent ? roleOLSContent.STUDENT : roleOLSContent.LEARNING_COACH;

return (
    <div className="navigating-ols-container">
        <div className="inner-container">
            <Breadcrumb items={breadcrumbItems} />
            <div className="subpage-hero-container">
                <SubPageHero
                    theme="secondary"
                    title="Navigating the K12 School (OLS)"
                    description={
                        <>
                           
                            <strong>You'll be able to access your K12 School (OLS) on your first day of school. </strong> 
                            <div className="hero-inner-content"> In the meantime, check out these tips, guides, and tools to help you start the year confident and prepared.</div>
                            {showK12SchoolCta ? (
                            <div className="hero-ols-btn">
                                <Button
                                label={olsButtonLabel}
                                icon={olsCardIcon}
                                iconPosition="after"
                                size="lg"
                                variant="fill"
                                width="fit-content"
                                color="primary"
                                linkUrl={k12SchoolHref ?? ''}
                                linkTarget="_blank"
                                className="ols_btn"
                                disabled={!k12SchoolHref}
                                />
                            </div>
                            ) : null}
                        </>
                        
                    }
                    image={heroUnion}
                />
            </div>
        </div>

        <section className="navigating-content">
            
            {/* Section 1: Getting Started */}
            <GettingStartedSection
                header={gettingStartedHeader}
                description={gettingStartedDescription}
                media={roleUI.media}
                buttonText={roleUI.buttonText}
                linkUrl={roleUI.linkUrl}
            />

            {/* Section 2: LC Support */}
            <StackedContent>
                <StackedContent.Col2 cols="4">
                    <Image src={olsMedia} fluid={true} />
                </StackedContent.Col2>
                <StackedContent.Col2 cols="6">
                    <h2 className="color-primary">{olsHeader}</h2>
                    <p className="body-m">{olsDescription}</p>
                    <div className="btn-section">
                    <Button
                    label={olsButtonText}
                    icon={<RedirectIcon />}
                    iconPosition="after"
                    size="lg"
                    variant="fill"
                    width="fit-content"
                    linkUrl={olsLinkUrl}
                    linkTarget="_blank"
                    />
                    </div>
                </StackedContent.Col2>
            </StackedContent>

            {/* Section 3: New Sections */}
            <OlsVideoSection
                header="Not Seeing Your Courses?"
                description={
                    <>
                    If you've logged into your K12 School (OLS) account and don't see your courses on your dashboard — don't worry! Our placement teams are working behind the scenes to ensure you're enrolled in the right courses. <br/><br/>
                    If you see an error message or your dashboard is empty, watch this video to find out more.
                    </>
                }
                videoUrl="wtrHFz5dHooVZv5g2UUCio"
                reverse
            />

            <OlsVideoSection
                header="Get Ready for Class"
                description={
                    <>
                        When schedules are ready, use the{" "}
                        {calendarLink ? (
                            <b>
                                <a
                                    href={calendarLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="canvas-link"
                                    style={{ color: "#1C1C1F" }}
                                >
                                    Calendar
                                </a>
                            </b>
                        ) : (
                            <>calendar</>
                        )}{" "}
                        in the K12 School (OLS) from your student's account to view class times and join live Class Connect sessions.
                        <br />
                        <br />
                        Before you join class, watch this video to learn how to test your settings, get connected, and make sure you’re all good.
                    </>
                }
                videoUrl="ERNZDZk4WBSmzwQaDtTJBo"
                reverse={false}
            />

            <OlsVideoSection
                header="Download the App"
                description={
                    <>
                    Connect the Canvas app to your K12 School (OLS) account to cruise through school. <br/> <br/>
                    First, find the "QR for Mobile Login" code on the Account page in the K12 School (OLS). Then, download the Canvas app from your app store, open the app and tap "QR Login” on the bottom. Point your camera's phone at the QR on your computer screen, and you'll be logged in automatically. And that's it!
                    </>
                }
                videoUrl="KQe2MpDPfXQHgrBUaALhNZ" 
                reverse={true}
            />

            {/* Section 3: Survival Guide (Grade-based) */}
            {survivalProps ? (
            <StackedContent>
                <StackedContent.Col2 cols="4">
                    <Image src={survivalProps.media} fluid={true} alt="Grade Wise" />
                </StackedContent.Col2>
                <StackedContent.Col1 cols="6">
                    <h2 className="color-primary">{survivalProps.header}</h2>
                    <p className="body-m">{survivalProps.description}</p>
                    <div className="btn-section">
                    <Button
                        label="Download the Survival Guide"
                        icon={<RedirectIcon />}
                        iconPosition="after"
                        size="lg"
                        variant="fill"
                        width="fit-content"
                        linkUrl={survivalProps.pdfLinkUrl}
                        linkTarget="_blank"
                    />
                    </div>
                </StackedContent.Col1>
            </StackedContent>
            ) : (
                <div className="survival-guides--empty" aria-hidden="true" />
            )}
        </section>

        {/* Section 4: Vertical Cards  */}
        <section className="verticalCardsLesson">
        {lessonProps ? (
            <StackedContent>
                <StackedContent.Col1 cols="5">
                    <Card
                    theme="secondary-light"
                    orientation="vertical"
                    header={lessonProps.header}
                    description={lessonProps.description}
                    media={sampleLessonImg}                 
                    buttonProps={{
                        label: "Start the Sample Lesson",
                        icon: <RedirectIcon />,
                        iconPosition: "after",
                        linkUrl:lessonProps.pdfLinkUrl ?? '',
                        linkTarget: "_blank",                   
                        className: "ols_btn_menu left_align_mobile"
                    }}
                    />
                </StackedContent.Col1>
                <StackedContent.Col2 cols="5">
                    <Card
                        theme="primary-light"
                        orientation="vertical"
                        header="Get Comfortable in the K12 School (OLS) "
                        description="Take a little time to click through the K12 School (OLS) on your own, learning what's available and where things are. Revisit our video tutorials and survival guides as often as needed to help you learn your way around. "
                        media={K5NavigateImg}
                        buttonProps={{
                            label: olsCardLabel,
                            icon: olsCardIcon,
                            iconPosition: "after",
                            linkUrl: k12SchoolHref ?? '',
                            linkTarget: "_blank",
                            className: "ols_btn_menu left_align_mobile border-ols-btn",
                            disabled: !k12SchoolHref,
                        }}
                    />
                </StackedContent.Col2>
            </StackedContent>
         ) : (
            <div className="sample-lesson-comfortable-card--empty" aria-hidden="true" />
        )}
        </section>

        {/* Section 5: K12 Zone 1 Block - Horizontal Card  */}
        {(userRoleVal === 'STUDENT' || userRoleVal === 'LEARNING_COACH') && showK12Zone && (
        <StackedContent>
            <StackedContent.Col1 cols="10">
                <Card
                theme="primary"
                orientation="horizontal"
                header="Hang Out in the K12 Zone"
                description="With K12's virtual campus, online school is more social than ever! Zones are split into grade bands so students can chat with classmates, play games with their peers, meet with teachers, and join video group activities."
                videoUuid="BkFWdmF61TADiRfTdJFTGJ"
                buttonProps={{
                    label: k12ButtonText,              
                    icon: <RedirectIcon />,
                    iconPosition: "after",
                    linkUrl: displayK12ZoneLink,
                    linkTarget: "_blank",
                    className: "navigate_k12_btn"
                }}
                />
            </StackedContent.Col1>
        </StackedContent>
        )}

        {/* Section 6: Online School FAQ */}
        <section className="section-navigating-FAQ">
        <StackedContent>
            <StackedContent.Col1 cols="10">
            <FAQSection title="Online School FAQs" faqs={onlineSchoolFaqs} />

            <ButtonSet padTop>
                <Button
                label="View All FAQs (PDF)"
                icon={<RedirectIcon />}
                iconPosition="after"
                size="lg"
                variant="border"
                width="fit-content"
                linkUrl={pdfSchoolFAQ}
                linkTarget="_blank"
                className="ols_btn_menu navigate_k12_btn"
                />

                <Button
                label="Watch the FAQs Video"
                size="lg"
                variant="border"
                width="fit-content"
                className="ols_btn_menu navigate_k12_btn"
                onClick={handleOpenLightbox}
                />
                {/* Hidden lightbox player instance for Watch the FAQs Video*/}
                <div style={{ display: "none" }}>
                <VidyardPlayer uuid={videoSchoolFAQ} type="lightbox" />
                </div>
            </ButtonSet>
            </StackedContent.Col1>
        </StackedContent>
        </section>


    </div>
    );
};
export default NavigatingK12School;
